/**
 * @file     ScheduleShowingModal.tsx
 * @purpose  Book a showing: a two-step listing sheet. Step 1 picks a date and
 *           a start time (Pacific); step 2 takes first name, last name, email
 *           and mobile phone with the one consent line. Sent offers Add to
 *           calendar (.ics) and See similar homes. An open-house tile opens it
 *           on step 2 with its time filled in. Posts to /api/schedule-showing,
 *           which files the lead and the CRM appointment; a new account gets
 *           its session in place, an existing one is never signed in.
 */
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { analyticsEvents } from '@/core/constants/analytics';
import { gatedSecureFetch } from '@/services/phone-gate/gatedFetch';
import { ContactFields } from '@/components/lead-capture/ContactFields';
import { RealtorQuestion } from '@/components/lead-capture/RealtorQuestion';
import { gateColors } from '@/components/gate/GateSheet';
import {
  ListingSheet,
  SheetFooter,
  SheetIcon,
  SheetLabel,
  SheetSent,
  SHEET_ICONS,
} from '@/components/lead-capture/ListingSheet';
import { contactPayload, validateContact, type ContactErrors, type ContactField, type ContactValues } from '@/services/leads/contactFields';
import { useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { layoutTokens, motionTokens } from '@/design-system/tokens';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { formatMonthDay, formatWeekdayShort } from '@/utils/dateFormat';
import { formatPhoneInput } from '@/utils/formatPhone';
import {
  SHOWING_SLOTS,
  clockLabel,
  downloadIcs,
  showingDays,
  showingIcs,
  showingSlotLine,
} from '@/utils/showingCalendar';

interface Props {
  open: boolean;
  onClose: () => void;
  listingTitle: string;
  listingSlug: string;
  listingMlsNumber: string | null;
  /** "YYYY-MM-DD": picks the day on step 1. */
  preselectedDate?: string | null;
  /** "HH:MM" Pacific. With a date, the sheet opens on step 2 (open-house tiles). */
  preselectedTime?: string | null;
  /** "HH:MM" end of an open house, for the calendar event. */
  preselectedEndTime?: string | null;
  analyticsSurface: 'slug_page' | 'map_sheet' | 'quick_view' | 'suggestions';
  /** Fires once when the showing request is successfully submitted, with the REALTOR® answer. */
  onSubmitted?: (workingWithRealtor: boolean) => void;
  listingPrice?: number | null;
  listingPlace?: string | null;
  listingThumb?: string | null;
  /** Sent screen: See similar homes. Without it, the second button is Done. */
  onSeeSimilar?: () => void;
}

type Step = 'pick' | 'details' | 'sent';
type AuthKind = 'new_account' | 'existing_account' | 'signed_in' | null;

const EMPTY: ContactValues = { firstName: '', lastName: '', email: '', phone: '' };
const motion = `${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`;

const tileSx = (pressed: boolean) => ({
  boxSizing: 'border-box' as const,
  borderRadius: `${layoutTokens.radius.md}px`,
  border: `1px solid ${pressed ? gateColors.primary : gateColors.borderStrong}`,
  backgroundColor: pressed ? gateColors.primary : gateColors.surface,
  color: pressed ? gateColors.onPrimary : gateColors.text,
  fontFamily: 'inherit',
  fontVariantNumeric: 'tabular-nums',
  transition: `background-color ${motion}, border-color ${motion}, color ${motion}`,
  '@media (hover: hover)': { '&:hover': pressed ? {} : { borderColor: gateColors.textSecondary } },
  '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
});

/** Signed-in visitors start with what their account already holds. */
export function accountContact(user: ReturnType<typeof useAuth>['user']): ContactValues {
  if (!user) return EMPTY;
  const meta = (user.user_metadata ?? {}) as { first_name?: string; last_name?: string; name?: string; phone?: string };
  const [first = '', ...rest] = (meta.name ?? '').trim().split(/\s+/);
  return {
    firstName: meta.first_name ?? first,
    lastName: meta.last_name ?? rest.join(' '),
    email: user.email ?? '',
    phone: meta.phone ? formatPhoneInput(meta.phone) : '',
  };
}

export function ScheduleShowingModal({
  open,
  onClose,
  listingTitle,
  listingSlug,
  listingMlsNumber,
  preselectedDate,
  preselectedTime,
  preselectedEndTime,
  analyticsSurface,
  onSubmitted,
  listingPrice,
  listingPlace,
  listingThumb,
  onSeeSimilar,
}: Props) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { user, isAuthenticated } = useAuth();
  const [step, setStep] = useState<Step>('pick');
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [contact, setContact] = useState<ContactValues>(EMPTY);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [failed, setFailed] = useState(false);
  const consent = useConsentChoices();
  const [realtor, setRealtor] = useState<boolean | null>(null);
  const [realtorError, setRealtorError] = useState(false);
  const [pending, setPending] = useState(false);
  const [authKind, setAuthKind] = useState<AuthKind>(null);
  const submittedRef = useRef(false);
  const analyticsListingKey = listingMlsNumber ?? listingSlug;
  const days = useMemo(() => {
    const list = open ? showingDays() : [];
    return preselectedDate && !list.includes(preselectedDate) ? [preselectedDate, ...list] : list;
  }, [open, preselectedDate]);

  useEffect(() => {
    if (!open) return;
    submittedRef.current = false;
    const openHouse = Boolean(preselectedDate && preselectedTime);
    setStep(openHouse ? 'details' : 'pick');
    setDirection('forward');
    setDate(preselectedDate ?? null);
    setTime(openHouse ? preselectedTime ?? null : null);
    setContact(accountContact(isAuthenticated ? user : null));
    setErrors({});
    setFailed(false);
    consent.reset();
    setRealtor(null);
    setRealtorError(false);
    setAuthKind(null);
    trackEvent({
      eventName: analyticsEvents.propertyShowingRequestOpened,
      eventType: 'click',
      category: 'property_detail',
      label: analyticsSurface,
      metadata: {
        listing_key: analyticsListingKey,
        listing_slug: listingSlug,
        surface: analyticsSurface,
        preselected_open_house: Boolean(preselectedDate),
      },
    });
    reltaTrack('showing.request.open', { listing_key: analyticsListingKey });
    // Once per open, not when the analytics callbacks are recreated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (pending) return;
    if (step !== 'sent' && !submittedRef.current) {
      trackEvent({
        eventName: analyticsEvents.propertyShowingRequestAbandoned,
        eventType: 'behavior',
        category: 'property_detail',
        label: analyticsSurface,
        metadata: {
          listing_key: analyticsListingKey,
          listing_slug: listingSlug,
          surface: analyticsSurface,
          step: step === 'pick' ? 'pick' : 'contact',
          selected_slot_count: time ? 1 : 0,
        },
      });
      reltaTrack('showing.request.abandon', { listing_key: analyticsListingKey, step });
    }
    onClose();
  };

  const go = (next: Step, dir: 'forward' | 'back') => {
    setDirection(dir);
    setStep(next);
  };

  const submit = async () => {
    if (!date || !time || pending) return;
    const found = validateContact(contact, t);
    setErrors(found);
    setRealtorError(realtor === null);
    if (Object.keys(found).length > 0 || realtor === null) return;
    const payload = contactPayload(contact);
    setFailed(false);
    setPending(true);
    try {
      const res = await gatedSecureFetch('/api/schedule-showing', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          listingSlug,
          listingTitle,
          listingMlsNumber,
          preferredDate: date,
          preferredTimes: [time],
          workingWithRealtor: realtor,
          ...consent.payload(payload.phone),
          contact: {
            firstName: payload.firstName,
            lastName: payload.lastName,
            email: payload.email,
            phone: payload.phone,
          },
        }),
      });
      if (!res.ok) throw new Error(`showing ${res.status}`);
      const data = (await res.json()) as { ok: boolean; kind?: AuthKind; session?: { access_token: string; refresh_token: string } };
      if (data.session && data.kind === 'new_account') {
        const { createSupabaseBrowserClient } = await import('@/services/supabase/client');
        await createSupabaseBrowserClient().auth.setSession(data.session);
      }
      submittedRef.current = true;
      setAuthKind(data.kind ?? 'signed_in');
      trackEvent({
        eventName: analyticsEvents.propertyShowingRequestSubmitted,
        eventType: 'conversion',
        category: 'property_detail',
        label: analyticsSurface,
        metadata: {
          listing_key: analyticsListingKey,
          listing_slug: listingSlug,
          surface: analyticsSurface,
          selected_slot_count: 1,
          authenticated: isAuthenticated,
          working_with_realtor: realtor,
        },
      });
      go('sent', 'forward');
      onSubmitted?.(realtor);
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  };

  const addToCalendar = () => {
    if (!date || !time) return;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    downloadIcs(
      `showing-${listingMlsNumber ?? listingSlug}.ics`,
      showingIcs({
        date,
        time,
        endTime: preselectedTime === time ? preselectedEndTime : null,
        title: t('showing.calendar.title', { defaultValue: 'Showing request: {{address}}', address: listingTitle }),
        location: listingTitle,
        description: t('showing.success.body', { defaultValue: 'Ray Rasouli will confirm the time with you, or suggest the next open time.' }),
        url: origin ? `${origin}/properties/${listingSlug}` : null,
        uid: `showing-${listingSlug}-${date}-${time.replace(':', '')}@relta.ca`,
      }),
    );
  };

  const slotLine = date && time ? showingSlotLine(date, time, t('showing.pacific', { defaultValue: 'Pacific' })) : '';
  const sheetListing = { title: listingTitle, price: listingPrice, place: listingPlace, thumbUrl: listingThumb };
  const title =
    step === 'pick'
      ? t('showing.pick.title', { defaultValue: 'Pick a time' })
      : step === 'details'
        ? t('showing.details.title', { defaultValue: 'Your details' })
        : t('showing.success.title', { defaultValue: 'Request sent' });

  return (
    <ListingSheet
      open={open}
      onClose={close}
      listing={sheetListing}
      title={title}
      titleHidden={step === 'sent'}
      steps={step === 'sent' ? undefined : { current: step === 'pick' ? 1 : 2, total: 2 }}
      onBack={step === 'details' ? () => go('pick', 'back') : undefined}
      stepKey={step}
      stepDirection={direction}
      testId="showing-sheet"
    >
      {step === 'pick' ? (
        <>
          <Box role="group" aria-labelledby="showing-date-label" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SheetLabel id="showing-date-label">{t('showing.pick.date', { defaultValue: 'Date' })}</SheetLabel>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', mx: -1, p: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              {days.map((iso) => {
                const pressed = date === iso;
                return (
                  <ButtonBase
                    key={iso}
                    aria-pressed={pressed}
                    aria-label={`${formatWeekdayShort(iso)}, ${formatMonthDay(iso)}`}
                    onClick={() => setDate(iso)}
                    sx={{ ...tileSx(pressed), flex: '0 0 auto', width: 72, height: 72, flexDirection: 'column', gap: 0.5 }}
                  >
                    <Box component="span" sx={{ fontSize: 12 }}>{formatWeekdayShort(iso)}</Box>
                    <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.semibold, whiteSpace: 'nowrap' }}>{formatMonthDay(iso)}</Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
          <Box role="group" aria-labelledby="showing-time-label" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SheetLabel id="showing-time-label">{t('showing.pick.time', { defaultValue: 'Start time, Pacific' })}</SheetLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 2 }}>
              {SHOWING_SLOTS.map((slot) => {
                const pressed = time === slot;
                return (
                  <ButtonBase
                    key={slot}
                    aria-pressed={pressed}
                    onClick={() => setTime(slot)}
                    sx={{ ...tileSx(pressed), height: 44, fontSize: 15, fontWeight: pressed ? fontWeightTokens.semibold : fontWeightTokens.medium }}
                  >
                    {clockLabel(slot)}
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>
          <SheetFooter
            cta={t('common.actions.continue', { defaultValue: 'Continue' })}
            disabled={!date || !time}
            onSubmit={() => go('details', 'forward')}
          />
        </>
      ) : step === 'details' ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              minHeight: 52,
              py: 1,
              pl: 3.5,
              pr: 1,
              borderRadius: `${layoutTokens.radius.sm}px`,
              backgroundColor: gateColors.surfaceSunken,
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', color: gateColors.muted }}>
              <SheetIcon d={SHEET_ICONS.calendar} />
            </Box>
            <Typography component="p" data-showing-slot sx={{ m: 0, flex: 1, minWidth: 0, fontSize: 15, fontWeight: fontWeightTokens.medium, fontVariantNumeric: 'tabular-nums' }}>
              {slotLine}
            </Typography>
            <ButtonBase
              onClick={() => go('pick', 'back')}
              sx={{
                minHeight: 44,
                px: 2.5,
                borderRadius: `${layoutTokens.radius.sm}px`,
                fontFamily: 'inherit',
                fontSize: 14.5,
                fontWeight: fontWeightTokens.medium,
                color: gateColors.link,
                '&.Mui-focusVisible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2 },
              }}
            >
              {t('showing.details.change', { defaultValue: 'Change' })}
            </ButtonBase>
          </Box>
          <ContactFields
            values={contact}
            errors={errors}
            onChange={(field: ContactField, value: string) => {
              setContact((prev) => ({ ...prev, [field]: value }));
              setErrors((prev) => ({ ...prev, [field]: undefined }));
            }}
            disabled={pending}
            idPrefix="showing"
          />
          <RealtorQuestion
            value={realtor}
            onChange={setRealtor}
            yesLine={t('forms.realtor.yesShowing', { defaultValue: 'The REALTOR® you work with can also book this for you.' })}
            error={realtorError}
            disabled={pending}
            idPrefix="showing"
          />
          <SheetFooter
            next={t('showing.details.next', { defaultValue: 'Ray Rasouli will reply by email or phone to confirm a time.' })}
            cta={t('showing.details.submit', { defaultValue: 'Send request' })}
            pending={pending}
            error={failed}
            onSubmit={() => void submit()}
            consent={{ action: t('forms.consent.sending', { defaultValue: 'sending' }) }}
            consentChoices={{ values: consent.values, onChange: consent.setValues, phone: true }}
          />
          <Typography component="p" sx={{ m: 0, fontSize: 12.5, lineHeight: 1.5, color: gateColors.muted }}>
            {t('showing.agencyNote', {
              defaultValue:
                'Ray Rasouli can show you this home. Ray Rasouli is not your agent unless you sign a written agreement, and a showing is not advice on whether to buy.',
            })}
          </Typography>
        </>
      ) : (
        <SheetSent
          title={title}
          meta={slotLine}
          line={t('showing.success.body', { defaultValue: 'Ray Rasouli will confirm the time with you, or suggest the next open time.' })}
          primary={{ label: t('showing.success.calendar', { defaultValue: 'Add to calendar' }), icon: SHEET_ICONS.calendar, onClick: addToCalendar }}
          secondary={
            onSeeSimilar
              ? {
                  label: t('listingActions.label.seeSimilar', { defaultValue: 'See similar homes' }),
                  onClick: () => {
                    onClose();
                    onSeeSimilar();
                  },
                }
              : { label: t('common.actions.done', { defaultValue: 'Done' }), onClick: onClose }
          }
        >
          {authKind === 'existing_account' ? (
            <Typography component="p" sx={{ m: 0, fontSize: 14, color: gateColors.textSecondary }}>
              {t('showing.success.existingAccount', { defaultValue: 'Sign in with this email to see your showings.' })}
            </Typography>
          ) : null}
        </SheetSent>
      )}
    </ListingSheet>
  );
}
